import { useEffect, useReducer, useRef, useState } from 'react';
import { ExpandableBox } from '@optimus-web/helpers';
import { ExpandIcon } from '../ExpandIcon';
import { Stack } from '../Stack';
import { Body, Typography } from '../typography';
import { ContentContainer, GradientElement, ToggleContainer } from './style';

interface ExpandableContentProps {
    children: React.ReactNode;
    expandedToggleText: React.ReactNode;
    collapsedToggleText: React.ReactNode;
    lines?: number;
    lineHeight?: number;
    backgroundColor?: string;
    hasPlaceholder?: boolean;
}

export const ExpandableContent = (props: ExpandableContentProps) => {
    const [expanded, toggleExpanded] = useReducer((state) => !state, false);
    const [contentHeight, setContentHeight] = useState(-1);

    const {
        children,
        expandedToggleText,
        collapsedToggleText,
        lines = 3,
        lineHeight = 21,
        backgroundColor,
        hasPlaceholder = false,
        ...other
    } = props;

    const divRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (divRef == null) {
            return;
        }
        const height = divRef.current?.clientHeight ?? -1;
        setContentHeight(height);
    }, [divRef.current?.clientHeight]);

    const collapsedSize = lines * lineHeight;
    const expandable = Math.floor(contentHeight) > collapsedSize;
    const gradient =
        !expanded && expandable ? (
            <GradientElement lineHeight={lineHeight} lines={lines} backgroundColor={backgroundColor} />
        ) : null;

    const toggleText = expanded ? expandedToggleText : collapsedToggleText;
    const toggleContent =
        expandable || hasPlaceholder ? (
            <ToggleContainer
                visible={!hasPlaceholder || expandable}
                onClick={toggleExpanded}
                type="button"
                data-test-toggle
            >
                <Stack spacing="100">
                    <ExpandIcon expanded={expanded} />
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {toggleText}
                    </Typography>
                </Stack>
            </ToggleContainer>
        ) : null;

    return (
        <div>
            <ExpandableBox expanded={expanded} collapsedSize={collapsedSize} renderZeroSize {...other}>
                <ContentContainer>
                    <div ref={divRef}>
                        <Body size="small" as="div">
                            {children}
                        </Body>
                        {gradient}
                    </div>
                </ContentContainer>
            </ExpandableBox>
            {toggleContent}
        </div>
    );
};
